/**
 * 학습지 용지 형식 (1단 일반 / 2단 시험지형).
 * 시험지형은 csat 레이아웃이 유일한 형식. 옛 school/csat/exam_two_column 값은 exam 으로 정규화한다.
 */

export const PAPER_FORMATS = Object.freeze(['single', 'exam']);

export const PAPER_FORMAT_OPTIONS = Object.freeze([
  { id: 'single', label: '일반' },
  { id: 'exam', label: '시험지형' },
]);

/** 저장된 옛 세그먼트·스타일 값 → 시험지형. */
const LEGACY_EXAM_FORMATS = Object.freeze([
  'exam', 'school', 'csat', 'exam_two_column', 'exam_csat',
]);

export const CSAT_FORM_OPTIONS = Object.freeze([
  { id: '', label: '없음' },
  { id: '홀수형', label: '홀수형' },
  { id: '짝수형', label: '짝수형' },
]);

export const CSAT_HEADER_KEYS = Object.freeze(['exam_name', 'period', 'form', 'copyright']);

const TWO_COL_STYLES = Object.freeze({
  exam_two_column: 'exam',
  exam_csat: 'exam',
});

export const FALLBACK_TOAST_BY_REASON = Object.freeze({
  too_few_questions: '문항이 적어 1단으로 만들었어요',
  no_questions: '문항이 적어 1단으로 만들었어요',
  sections_mode: '활동지는 1단으로 만들어요',
  worksheet_type: '이 학습지 유형은 1단으로 만들어요',
  paper_size: '이 용지 크기에서는 1단으로 만들었어요',
  columns_hint_1: '일반 형식으로 만들었어요',
});

export const DEFAULT_FALLBACK_TOAST = '문항이 적어 1단으로 만들었어요';

export const IMAGE_MODE_PAPER_HINT = '이미지 모드에서는 일반 형식으로 만들어요';

const asHint = (outline) => (
  outline && typeof outline.layout_hint === 'object' && outline.layout_hint ? outline.layout_hint : {}
);

export function normalizePaperFormat(value) {
  if (value === 'single') return 'single';
  if (LEGACY_EXAM_FORMATS.includes(value)) return 'exam';
  return 'single';
}

export function paperFormatFromOutline(outline) {
  if (!outline || typeof outline !== 'object') return 'single';
  const hint = asHint(outline);
  const style = String(outline.worksheet_style || '').toLowerCase();
  const hintPaper = String(hint.paper_style || '').toLowerCase();
  const hintCols = hint.columns;
  const columns = hintCols === 2 || hintCols === '2' ? 2 : (hintCols === 1 || hintCols === '1' ? 1 : null);

  if (style === 'exam_csat' || style === 'exam_two_column') return 'exam';
  if (hintPaper === 'csat' || hintPaper === 'school') return 'exam';
  if (columns === 2) return 'exam';
  return 'single';
}

export function worksheetStyleForPaperFormat(format, fallbackStyle = 'exam') {
  if (format === 'exam') return 'exam_csat';
  const fallback = String(fallbackStyle || 'exam').toLowerCase();
  if (TWO_COL_STYLES[fallback]) return 'exam';
  return fallback || 'exam';
}

export function layoutHintPatchForPaperFormat(format) {
  if (format === 'exam') return { columns: 2, paper_style: 'csat' };
  return { columns: 1, paper_style: null };
}

export function mergeLayoutHint(existing, patch) {
  const merged = { ...(existing && typeof existing === 'object' ? existing : {}) };
  Object.entries(patch || {}).forEach(([key, value]) => {
    if (value === null || value === undefined) delete merged[key];
    else merged[key] = value;
  });
  return merged;
}

export function csatHeaderFromOutline(outline) {
  const info = outline && typeof outline.header_info === 'object' && outline.header_info
    ? outline.header_info
    : {};
  return {
    exam_name: typeof info.exam_name === 'string' ? info.exam_name : '',
    period: typeof info.period === 'string' ? info.period : '',
    form: typeof info.form === 'string' ? info.form : '',
    copyright: typeof info.copyright === 'string' ? info.copyright : '',
  };
}

/** 빈 값은 키 자체를 넣지 않는다 (백엔드 기본 = 미출력). */
export function compactCsatHeader(examInfo) {
  const out = {};
  CSAT_HEADER_KEYS.forEach((key) => {
    const value = String(examInfo?.[key] || '').trim();
    if (value) out[key] = value;
  });
  return out;
}

export function applyPaperFormatToOutline(outline, format, { examInfo, fallbackStyle } = {}) {
  const source = outline && typeof outline === 'object' ? outline : {};
  const resolvedFormat = normalizePaperFormat(format);
  const layout_hint = mergeLayoutHint(source.layout_hint, layoutHintPatchForPaperFormat(resolvedFormat));
  const next = {
    ...source,
    worksheet_style: worksheetStyleForPaperFormat(resolvedFormat, fallbackStyle || source.worksheet_style),
    layout_hint,
  };
  if (resolvedFormat === 'exam' && examInfo) {
    const compact = compactCsatHeader(examInfo);
    const header = { ...(source.header_info && typeof source.header_info === 'object' ? source.header_info : {}) };
    CSAT_HEADER_KEYS.forEach((key) => {
      if (compact[key]) header[key] = compact[key];
      else delete header[key];
    });
    next.header_info = header;
  }
  return next;
}

export function resolveWorksheetExportLayout(outline, format = null) {
  const resolvedFormat = format ? normalizePaperFormat(format) : paperFormatFromOutline(outline);
  if (resolvedFormat === 'exam') return { columns: 2, paperStyle: 'csat' };
  return { columns: 1, paperStyle: null };
}

export function fallbackToastMessage(reason) {
  if (!reason) return '';
  return FALLBACK_TOAST_BY_REASON[reason] || DEFAULT_FALLBACK_TOAST;
}

export function readFallbackReason(payload) {
  const applied = payload?.layout_hint_applied
    || payload?.outline?.layout_hint_applied
    || null;
  const reason = applied && typeof applied.fallback_reason === 'string'
    ? applied.fallback_reason
    : '';
  return reason || '';
}
